import { Timezone } from '@/datetime/timezone.service';
import { Moment } from 'moment-timezone';
import { Instance } from 'flatpickr/dist/types/instance';
import { useCallback, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import { createFooterButtons } from '@/datetime/flatpickr/flatpickrButtons.util';
import { useTranslation } from 'react-i18next';
import { positionFlatpickrCalendar } from '@/datetime/flatpickr/flatpickrPositioning.util';
import { useEffectOnceWhen } from 'rooks';
import {
  areFormattedDatesEqual,
  momentToTimezoneNaiveDate,
  timezoneNaiveDateToMoment,
} from '@/datetime/flatpickr/flatpickrTimezones.util';

export const useFlatpickr = (
  inputRef: React.RefObject<HTMLInputElement | null>,
  value: Moment,
  timezone: Timezone,
  localizedDateFormat: string,
  enableSeconds: boolean,
  enableTime: boolean,
  enableTime24hr: boolean,
  onDateSelect: (date: Moment) => void,
  onManualInput: (input: string) => Moment,
  isInsideModal: boolean,
) => {
  const { t } = useTranslation();
  const fpRef = useRef<Instance | null>(null);
  const buttonCleanupRef = useRef<(() => void) | null>(null);

  const formatDate = useCallback(
    (date: Date, _format?: string): string => {
      const targetMoment = timezoneNaiveDateToMoment(date, timezone);
      return targetMoment.format(localizedDateFormat);
    },
    [timezone, localizedDateFormat],
  );

  const parseDate = useCallback(
    (dateString: string, _format?: string, _timeless?: boolean): Date => {
      const parsedMoment = onManualInput(dateString);
      const timezoneNaiveDate = momentToTimezoneNaiveDate(parsedMoment, timezone);
      fpRef.current?.setDate(timezoneNaiveDate, false);
      return timezoneNaiveDate;
    },
    [onManualInput, timezone],
  );

  const handleReady = useCallback(
    (_dates: Date[], _dateStr: string, instance: Instance) => {
      const onCancel = () => {
        const resetDate = momentToTimezoneNaiveDate(value, timezone);
        instance.setDate(resetDate, false);
        instance.close();
      };
      buttonCleanupRef.current = createFooterButtons(instance, t, onCancel);
    },
    [value.valueOf(), timezone, t],
  );

  const handleDateChange = useCallback(
    (selectedDates: Date[]) => {
      if (selectedDates.length > 0) {
        const selectedDate = selectedDates[0];
        const zonedDate = timezoneNaiveDateToMoment(selectedDate, timezone);
        onDateSelect(zonedDate);
      }
    },
    [onDateSelect, timezone],
  );

  const getModalContainer = useCallback((): HTMLElement | null => {
    return (inputRef.current?.closest('[data-state="open"][role="dialog"]') as HTMLElement) || null;
  }, [inputRef]);

  /**
   * Sync flatpickr display when value or timezone changes
   *
   * This ensures the input field shows the correct formatted time
   * when props change externally (e.g., timezone switches, programmatic date updates).
   */
  useEffect(() => {
    if (fpRef.current && value) {
      const currentlyDisplayedValue = fpRef.current.input.value;
      const needsSynced = !areFormattedDatesEqual(currentlyDisplayedValue, value, timezone, localizedDateFormat);
      if (needsSynced) {
        const jsDate = momentToTimezoneNaiveDate(value, timezone);
        fpRef.current.setDate(jsDate, false);
      }
    }
  }, [value.valueOf(), timezone, localizedDateFormat]);

  useEffect(() => {
    if (fpRef.current) {
      fpRef.current.config.parseDate = parseDate;
      fpRef.current.config.onChange = [handleDateChange];
      fpRef.current.config.formatDate = formatDate;
    }
  }, [parseDate, handleDateChange, formatDate]);

  useEffectOnceWhen(() => {
    if (!inputRef.current) return;

    const appendTo = isInsideModal ? getModalContainer() : null;
    const initialDate = momentToTimezoneNaiveDate(value, timezone);

    fpRef.current = flatpickr(inputRef.current, {
      ...(!!appendTo && {
        appendTo,
        position: (fp: Instance, relativeEl: HTMLElement | undefined) => {
          positionFlatpickrCalendar(fp, relativeEl, appendTo);
        },
      }),
      allowInput: true,
      clickOpens: true,
      closeOnSelect: true,
      dateFormat: 'Z', // Use ISO format internally
      defaultDate: initialDate,
      disableMobile: true,
      enableSeconds,
      enableTime,
      formatDate,
      parseDate,
      time_24hr: enableTime24hr,
      onChange: handleDateChange,
      onClose: () => {
        inputRef.current?.blur();
      },
      onReady: handleReady,
    });

    return () => {
      buttonCleanupRef.current?.();
      buttonCleanupRef.current = null;
      fpRef.current?.destroy();
      fpRef.current = null;
    };
  }, !!inputRef.current);

  return fpRef;
};
